import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './TeacherGradeRequestsView.css';

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const asUuid = (v) => (typeof v === 'string' && UUID_RE.test(v) ? v : null);

// iOS decodes dates with ISO8601DateFormatter (.withInternetDateTime) which does
// NOT accept fractional seconds — strip ".SSS" so the string parses on iOS.
const toIsoString = (d) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' }
];

function TeacherGradeRequestsView({ teacherData }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [classAssignments, setClassAssignments] = useState([]);
  const [formData, setFormData] = useState({
    classId: '',
    studentId: '',
    assignmentId: '',
    currentGrade: '',
    requestedGrade: '',
    reason: ''
  });

  const loadRequests = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }
    try {
      const requestsQuery = query(
        collection(db, 'gradeChangeRequests'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(requestsQuery);
      const requestsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      requestsData.sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bTime - aTime;
      });
      setRequests(requestsData);
    } catch (error) {
      console.error('Error loading grade requests:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [teacherData]);

  const loadClasses = useCallback(async () => {
    if (!teacherData?.uid) return;
    try {
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      let classesData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      if (teacherData.specialtySubject) {
        const allClassesQuery = query(collection(db, 'classes'));
        const allSnapshot = await getDocs(allClassesQuery);
        const allClasses = allSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const subjectClasses = allClasses.filter(c =>
          (c.subjectTeacherAssignments || []).includes(teacherData.specialtySubject) &&
          !classesData.find(ec => ec.id === c.id)
        );
        classesData = [...classesData, ...subjectClasses];
      }

      setClasses(classesData);
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  }, [teacherData]);

  useEffect(() => {
    loadRequests();
    loadClasses();
  }, [loadRequests, loadClasses]);

  const loadClassStudents = async (classId) => {
    if (!classId) {
      setClassStudents([]);
      return;
    }
    try {
      const studentsQuery = query(
        collection(db, 'classes', classId, 'students'),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(studentsQuery);
      setClassStudents(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading students:', error);
      setClassStudents([]);
    }
  };

  const loadClassAssignments = async (classId) => {
    if (!classId) {
      setClassAssignments([]);
      return;
    }
    try {
      const assignmentsQuery = query(collection(db, 'classes', classId, 'assignments'));
      const snapshot = await getDocs(assignmentsQuery);
      setClassAssignments(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading assignments:', error);
      setClassAssignments([]);
    }
  };

  const handleClassChange = (classId) => {
    setFormData(prev => ({ ...prev, classId, studentId: '', assignmentId: '' }));
    loadClassStudents(classId);
    loadClassAssignments(classId);
  };

  const resetForm = () => {
    setFormData({
      classId: '',
      studentId: '',
      assignmentId: '',
      currentGrade: '',
      requestedGrade: '',
      reason: ''
    });
    setClassStudents([]);
    setClassAssignments([]);
    setShowAddForm(false);
  };

  const handleSubmitRequest = async () => {
    if (!formData.classId || !formData.studentId || !formData.assignmentId) {
      alert('Please select a class, student, and assignment.');
      return;
    }
    if (!formData.currentGrade.trim() || !formData.requestedGrade.trim()) {
      alert('Please enter both current and requested grades.');
      return;
    }
    if (!formData.reason.trim()) {
      alert('Please provide a reason for the grade change.');
      return;
    }

    setSaving(true);
    try {
      const selectedClass = classes.find(c => c.id === formData.classId);
      const selectedStudent = classStudents.find(s => s.id === formData.studentId);
      const selectedAssignment = classAssignments.find(a => a.id === formData.assignmentId);

      const nowIso = toIsoString(new Date());
      const requestId = crypto.randomUUID();
      const teacherUuid = asUuid(teacherData?.uuid) || teacherData?.uid;
      const studentUuid = asUuid(selectedStudent?.id) || asUuid(selectedStudent?.uuid) || formData.studentId;
      const assignmentUuid = asUuid(selectedAssignment?.id) || formData.assignmentId;
      const studentName = selectedStudent?.fullName || `${selectedStudent?.firstName || ''} ${selectedStudent?.lastName || ''}`.trim() || '';
      const teacherName = teacherData.displayName || teacherData.username || '';
      const schoolIdUuid = asUuid(teacherData?.schoolId);

      // iOS nests requests under the grade doc: classes/{classId}/assignments/
      // {assignmentId}/grades/{gradeId}/changeRequests/{requestId}. Find the
      // existing grade for this student+assignment; create a minimal grade doc
      // when none exists so the request has a parent doc admin approval can apply.
      const gradesCol = collection(db, 'classes', formData.classId, 'assignments', formData.assignmentId, 'grades');
      const gradesSnap = await getDocs(gradesCol);
      const gradeDocSnap = gradesSnap.docs.find(d => {
        const sid = d.data().studentId;
        return sid === formData.studentId || sid === studentUuid || sid === selectedStudent?.studentId;
      });

      let gradeId;
      let gradeIdField;
      let originalScore = parseFloat(formData.currentGrade) || 0;
      let maxScore = selectedAssignment?.maxScore ?? 100;
      let originalFeedback = '';
      if (gradeDocSnap) {
        gradeId = gradeDocSnap.id;
        const gd = gradeDocSnap.data();
        gradeIdField = asUuid(gd.id) || gradeId;
        if (typeof gd.score === 'number') originalScore = gd.score;
        if (typeof gd.maxScore === 'number' && gd.maxScore > 0) maxScore = gd.maxScore;
        originalFeedback = gd.feedback || '';
      } else {
        gradeId = crypto.randomUUID();
        gradeIdField = gradeId;
        await setDoc(doc(gradesCol, gradeId), {
          id: gradeId,
          studentId: studentUuid,
          assignmentId: assignmentUuid,
          classId: formData.classId,
          score: originalScore,
          maxScore: maxScore,
          feedback: '',
          gradedBy: teacherUuid,
          gradedAt: nowIso,
          schoolId: teacherData?.schoolId || null
        });
      }

      // iOS GradeChangeRequest contract (Models/GradeChangeRequest.swift):
      // UUID strings for id/gradeId/studentId/assignmentId/requestedBy, Double
      // scores, Title-case status, ISO-8601 requestedAt.
      const requestDoc = {
        id: requestId,
        gradeId: gradeIdField,
        studentId: studentUuid,
        assignmentId: assignmentUuid,
        requestedBy: teacherUuid,
        originalScore: originalScore,
        originalMaxScore: maxScore,
        newScore: parseFloat(formData.requestedGrade) || 0,
        newMaxScore: maxScore,
        originalFeedback: originalFeedback,
        newFeedback: '',
        reason: formData.reason.trim(),
        status: 'Pending',
        requestedAt: nowIso,
        // Web display fields (ignored by the iOS decoder, read by AdminApprovalsView)
        studentName: studentName,
        className: selectedClass?.name || '',
        assignmentTitle: selectedAssignment?.title || '',
        teacherName: teacherName,
        teacherId: teacherData.uid,
        studentDocumentId: formData.studentId,
        classId: formData.classId,
        currentGrade: formData.currentGrade.trim(),
        requestedGrade: formData.requestedGrade.trim()
      };
      // schoolId is optional UUID? on iOS — a non-UUID value would fail decode; omit it
      if (schoolIdUuid) requestDoc.schoolId = schoolIdUuid;

      await setDoc(
        doc(db, 'classes', formData.classId, 'assignments', formData.assignmentId, 'grades', gradeId, 'changeRequests', requestId),
        requestDoc
      );

      // Minimal top-level mirror so web badge counts/analytics that read
      // `gradeChangeRequests` (and this view's own list) keep working.
      // status stays lowercase 'pending' — the dashboards filter `=== 'pending'`.
      await setDoc(doc(db, 'gradeChangeRequests', requestId), {
        id: requestId,
        schoolId: teacherData.schoolId || null,
        status: 'pending',
        requestedAt: nowIso,
        createdAt: nowIso,
        classId: formData.classId,
        assignmentId: formData.assignmentId,
        gradeId: gradeId,
        studentId: formData.studentId,
        studentName: studentName,
        teacherId: teacherData.uid,
        teacherName: teacherName,
        className: selectedClass?.name || '',
        assignmentTitle: selectedAssignment?.title || '',
        currentGrade: formData.currentGrade.trim(),
        requestedGrade: formData.requestedGrade.trim(),
        reason: formData.reason.trim()
      });

      resetForm();
      await loadRequests();
    } catch (error) {
      console.error('Error creating grade request:', error);
      alert('Failed to create grade request. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  const getStatusClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'approved') return 'approved';
    if (s === 'rejected') return 'rejected';
    return 'pending';
  };

  const filteredRequests = requests.filter(r => {
    if (statusFilter === 'all') return true;
    return (r.status || 'pending').toLowerCase() === statusFilter;
  });

  if (loading) {
    return (
      <div className="teacher-grade-requests-view">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="teacher-grade-requests-view">
      <h2>Grade Change Requests</h2>

      <div className="grade-requests-controls">
        <div className="filter-bar">
          {STATUS_FILTERS.map(filter => (
            <button
              key={filter.value}
              className={`filter-button ${statusFilter === filter.value ? 'active' : ''}`}
              onClick={() => setStatusFilter(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <button className="add-request-button" onClick={() => setShowAddForm(true)}>
          + New Request
        </button>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✏️</div>
          <h3>No Grade Change Requests</h3>
          <p>No grade change requests found. Click "New Request" to create one.</p>
        </div>
      ) : (
        <div className="requests-list">
          {filteredRequests.map(request => (
            <div key={request.id} className="request-card">
              <div className="request-header">
                <div className="request-student-info">
                  <h3>{request.studentName || 'Unknown Student'}</h3>
                  <span className="request-class">{request.className || 'Unknown Class'}</span>
                </div>
                <span className={`status-badge ${getStatusClass(request.status)}`}>
                  {request.status || 'pending'}
                </span>
              </div>
              <div className="request-body">
                <div className="request-assignment">
                  <span className="request-label">Assignment:</span>
                  <span>{request.assignmentTitle || 'N/A'}</span>
                </div>
                <div className="request-grades">
                  <div className="grade-display">
                    <span className="grade-label">Current Grade</span>
                    <span className="grade-value current">{request.currentGrade}</span>
                  </div>
                  <span className="grade-arrow">→</span>
                  <div className="grade-display">
                    <span className="grade-label">Requested Grade</span>
                    <span className="grade-value requested">{request.requestedGrade}</span>
                  </div>
                </div>
                <div className="request-reason">
                  <span className="request-label">Reason:</span>
                  <p>{request.reason}</p>
                </div>
                <div className="request-date">
                  <span>📅 {formatDate(request.createdAt)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddForm && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) resetForm(); }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>New Grade Change Request</h3>
              <button className="close-button" onClick={resetForm}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Class *</label>
                <select
                  value={formData.classId}
                  onChange={(e) => handleClassChange(e.target.value)}
                >
                  <option value="">Select a class</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name} {cls.grade ? `- ${cls.grade}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Student *</label>
                <select
                  value={formData.studentId}
                  onChange={(e) => setFormData(prev => ({ ...prev, studentId: e.target.value }))}
                  disabled={!formData.classId}
                >
                  <option value="">Select a student</option>
                  {classStudents.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.fullName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Assignment *</label>
                <select
                  value={formData.assignmentId}
                  onChange={(e) => setFormData(prev => ({ ...prev, assignmentId: e.target.value }))}
                  disabled={!formData.classId}
                >
                  <option value="">Select an assignment</option>
                  {classAssignments.map(assignment => (
                    <option key={assignment.id} value={assignment.id}>
                      {assignment.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Current Grade *</label>
                  <input
                    type="text"
                    value={formData.currentGrade}
                    onChange={(e) => setFormData(prev => ({ ...prev, currentGrade: e.target.value }))}
                    placeholder="e.g., 75"
                  />
                </div>
                <div className="form-group">
                  <label>Requested Grade *</label>
                  <input
                    type="text"
                    value={formData.requestedGrade}
                    onChange={(e) => setFormData(prev => ({ ...prev, requestedGrade: e.target.value }))}
                    placeholder="e.g., 85"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Reason *</label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Explain why this grade change is being requested"
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={resetForm}>Cancel</button>
              <button
                className="confirm-button"
                onClick={handleSubmitRequest}
                disabled={saving}
              >
                {saving ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherGradeRequestsView;
